import React, { useRef, useMemo, useEffect, useState } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'

function RainbowCoin({ 
  scale = 1, 
  position = [0, 0, 0], 
  rainbowSpeed = 1,
  glowIntensity = 2,
  ...props 
}) {
  const meshRef = useRef()
  const materialRef = useRef()
  const { size, viewport } = useThree()
  const [mouse, setMouse] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  
  // Load the GLB model
  const { scene } = useGLTF('/models/rarecoin.glb')
  const clonedScene = useMemo(() => scene.clone(), [scene])
  
  // Create a simple rainbow material using MeshStandardMaterial with animated color
  const rainbowMaterial = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      color: new THREE.Color('#ff00ff'),
      metalness: 0.8,
      roughness: 0.2,
      emissive: new THREE.Color('#ff00ff'),
      emissiveIntensity: 0.5
    })
  }, [])

  // Mouse event handlers
  const handlePointerDown = (event) => {
    setIsDragging(true)
    setMouse({ 
      x: (event.clientX / size.width) * 2 - 1, 
      y: -(event.clientY / size.height) * 2 + 1 
    })
  }

  const handlePointerMove = (event) => {
    if (isDragging && meshRef.current) {
      const newMouse = { 
        x: (event.clientX / size.width) * 2 - 1, 
        y: -(event.clientY / size.height) * 2 + 1 
      }
      
      // Calculate rotation based on mouse movement
      const deltaX = newMouse.x - mouse.x
      const deltaY = newMouse.y - mouse.y
      
      meshRef.current.rotation.y += deltaX * 3
      meshRef.current.rotation.x += deltaY * 3
      
      setMouse(newMouse)
    }
  }

  const handlePointerUp = () => {
    setIsDragging(false)
  }

  // Add global mouse event listeners
  useEffect(() => {
    const handleGlobalMouseMove = (event) => handlePointerMove(event)
    const handleGlobalMouseUp = () => handlePointerUp()
    
    if (isDragging) {
      window.addEventListener('mousemove', handleGlobalMouseMove)
      window.addEventListener('mouseup', handleGlobalMouseUp)
    }
    
    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove)
      window.removeEventListener('mouseup', handleGlobalMouseUp)
    }
  }, [isDragging, mouse])

  // Animate the colors and auto-rotation
  useFrame((state) => {
    if (meshRef.current) {
      // Auto-rotate the coin slowly when not dragging
      if (!isDragging) {
        meshRef.current.rotation.y += 0.01
        meshRef.current.rotation.x += 0.005
      }
      
      // Animate rainbow colors
      const time = state.clock.elapsedTime * rainbowSpeed
      const hue = (Math.sin(time) * 0.5 + 0.5)
      const color = new THREE.Color().setHSL(hue, 1, 0.5)
      const emissiveColor = new THREE.Color().setHSL(hue, 1, 0.3)
      
      if (materialRef.current) {
        materialRef.current.color = color
        materialRef.current.emissive = emissiveColor
        materialRef.current.emissiveIntensity = glowIntensity * 0.3
      }
    }
  })

  // Apply the material to all meshes
  useEffect(() => {
    if (clonedScene) {
      clonedScene.traverse((child) => {
        if (child.isMesh) {
          child.material = rainbowMaterial
          materialRef.current = rainbowMaterial
        }
      })
    }
  }, [clonedScene, rainbowMaterial])

  // Handle hover cursor
  const handlePointerOver = () => {
    document.body.style.cursor = 'grab'
  }

  const handlePointerOut = () => {
    document.body.style.cursor = 'default'
  }

  return (
    <primitive 
      ref={meshRef} 
      object={clonedScene} 
      scale={scale}
      position={position}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
      onPointerDown={handlePointerDown}
      style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      {...props}
    />
  )
}

// Preload the model
useGLTF.preload('/models/rarecoin.glb')

export default RainbowCoin
